import { PermissionEnum, TransactionTypeEnum } from '@ui/src';

export enum CustomerType {
  INDIVIDUAL = 'INDIVIDUAL',
  CORPORATE = 'CORPORATE',
  COMPANY_OFFICIAL = 'COMPANY_OFFICIAL',
}

export enum DocumentStatus {
  NEW = 'NEW',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
}

export interface Document {
  name: string;
  uploadId: string;
  category: string;
  expirationDate: string;
  status: DocumentStatus;
  organizationId?: string;
  userId: string;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  upload?: {
    mimeType?: string;
  };
  countComments?: {
    count: number;
  };
}

export enum ChannelType {
  MAIN = 'MAIN',
  NOTIFICATION = 'NOTIFICATION',
  APPLICATION = 'APPLICATION',
  TRANSACTION = 'TRANSACTION',
  DEMO = 'DEMO',
}

export enum ChannelStatus {
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
}

export enum MessageType {
  TEXT = 'TEXT',
  DOCUMENT = 'DOCUMENT',
}

export interface Channel {
  applicationId?: string;
  application?: {
    companyName?: string;
    firstName?: string;
    lastName?: string;
    type?: CustomerType;
  };
  messages: {
    edges: {
      node: {
        content: string;
        channelId: string;
        createdAt: string;
        document?: {
          name: string;
        };
      };
    }[];
  };
  channelType: ChannelType;
  closureType: string;
  createdAt: string;
  deletedAt?: string;
  employee?: {
    profile?: {
      firstName?: string;
      lastName?: string;
    };
    email?: string;
  };
  employeeId?: string;
  id: string;
  organization?: {
    companyName?: string;
    firstName?: string;
    lastName?: string;
    type?: CustomerType;
  };
  user?: {
    phone?: string;
    email?: string;
    profile?: {
      dob?: string;
      firstName?: string;
      lastName?: string;
    };
  };
  userId?: string;
  organizationId?: string;
  status: ChannelStatus;
  title: string;
  transactionId?: string;
  transaction: {
    typeId?: TransactionTypeEnum;
  };
  updatedAt?: string;
  hasUnread?: boolean;
}

export interface Message {
  channelId: string;
  type: MessageType;
  content?: string;
  readAt?: string;
  userId?: string;
  user?: {
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  employee?: {
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  employeeId?: string;
  organizationId?: string;
  documentId?: string;
  document?: {
    id: string;
    name: string;
    uploadId?: string;
    upload?: {
      id: string;
      mimeType: string;
      size: number;
    };
  };
  id?: string;
  createdAt: string;
  updatedAt?: string;
  edited?: boolean;
}

export interface Employee {
  id: string;
  profile: {
    firstName: string;
    lastName: string;
  };
  permissions?: PermissionEnum[];
  employeePermissions: {
    permissions: PermissionEnum[];
  }[];
  email?: string;
  status: string;
  highlight?: React.JSX.Element;
}

export interface UploadResponse {
  userId: string;
  mimeType: string;
  size: number;
  id: string;
  createdAt: string;
  name: string;
}

export interface TopUpInstruction {
  beneficiary: string;
  beneficiaryAddress: string;
  beneficiaryAccountNumber: string[];
  beneficiaryBankName: string;
  beneficiaryBankBIC: string;
  purposeOfPayment: string;
  paymentType: string;
}

export interface TopUpInstructions {
  items: TopUpInstruction[];
}

export enum PspPartnerPaymentType {
  FASTER_PAYMENT = 'FASTER_PAYMENT',
  BACS = 'BACS',
  SEPA_SCT_INST = 'SEPA_SCT_INST',
  SEPA_SCT = 'SEPA_SCT',
  TARGET_2 = 'TARGET_2',
  SWIFT = 'SWIFT',
  FEDWIRE = 'FEDWIRE',
  CRYPTO_ASSET_PAYMENT = 'CRYPTO_ASSET_PAYMENT',
  CASH_OVER_COUNTER = 'CASH_OVER_COUNTER',
}

export enum ApplicationStatus {
  INCOMPLETE = 'INCOMPLETE',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  READY_TO_REVIEW = 'READY_TO_REVIEW',
  REVIEW_IN_PROGRESS = 'REVIEW_IN_PROGRESS',
}

export interface ApplicationItem {
  firstName: string;
  lastName: string;
  dob: string;
  organizationId: string;
  dateOfIncorporation: string;
  id: string;
  companyName: string;
  type: CustomerType;
  status: ApplicationStatus;
  email?: string;
  phone?: string;
  userId?: string;
  applicationId?: string;
}

export enum CustomerDocumentType {
  APPLICATION_INCORPORATION_CERTIFICATE = 'APPLICATION_INCORPORATION_CERTIFICATE',
  APPLICATION_ARTICLES_OF_ASSOCIATIONS = 'APPLICATION_ARTICLES_OF_ASSOCIATIONS',
  APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS = 'APPLICATION_REGISTRY_OF_THE_SHAREHOLDERS',
  APPLICATION_REGISTRY_OF_THE_DIRECTORS = 'APPLICATION_REGISTRY_OF_THE_DIRECTORS',
  APPLICATION_ANNUAL_ACCOUNTS = 'APPLICATION_ANNUAL_ACCOUNTS',
  CLIENT_IDENTIFICATION = 'CLIENT_IDENTIFICATION',
  CLIENT_TRANSACTION = 'CLIENT_TRANSACTION',
  CLIENT_PSP_CUSTOMER = 'CLIENT_PSP_CUSTOMER',
  CLIENT_CDD_CHECK = 'CLIENT_CDD_CHECK',
  OTHER = 'OTHER',
}

export interface UseFilteredListProps {
  list: Employee[];
  searchQuery?: string;
}
